import type { FC } from "react";
import {
  Box,
  Container,
  Divider,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import InstagramIcon from "@mui/icons-material/Instagram";
import TwitterIcon from "@mui/icons-material/Twitter";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import { RouterLink } from "src/components/router-link";
import { paths } from "src/paths";

const legal = [
  { title: "Terms & Conditions", path: "/term-condition" },
  { title: "Privacy Policy",     path: "/privacy-policy" },
  { title: "Disclaimer",         path: "/disclaimer" },
];

const company = [
  { title: "Services",     id: "services" },
  { title: "OTC Desk",     id: "how-it-works" },
  { title: "How It Works", id: "how-it-works" },
  { title: "Contact",      id: "contact" },
];

const socials = [
  {
    icon: <InstagramIcon sx={{ fontSize: 18 }} />,
    href: "https://www.instagram.com/platinx.exchange",
    label: "Instagram",
    color: "#E1306C",
  },
  {
    icon: <TwitterIcon sx={{ fontSize: 18 }} />,
    href: "https://x.com/PlatinXExchange",
    label: "Twitter / X",
    color: "#1DA1F2",
  },
  {
    icon: <LinkedInIcon sx={{ fontSize: 18 }} />,
    href: "https://www.linkedin.com/company/platinx-exchange",
    label: "LinkedIn",
    color: "#0A66C2",
  },
];

function scrollTo(id: string) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

const ColHead: FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    sx={{
      color: "rgba(255,255,255,0.35)",
      fontSize: "0.68rem",
      fontWeight: 700,
      letterSpacing: "0.14em",
      textTransform: "uppercase",
      mb: 2.5,
    }}
  >
    {children}
  </Typography>
);

export const Footer: FC = () => (
  <Box
    component="footer"
    sx={{
      background: "linear-gradient(180deg, #080F1E 0%, #050B18 100%)",
      borderTop: "1px solid rgba(59, 130, 246, 0.1)",
      pt: { xs: 8, md: 12 },
      pb: 5,
      position: "relative",
      overflow: "hidden",
    }}
  >
    {/* Subtle background grid */}
    <Box
      sx={{
        position: "absolute", inset: 0, pointerEvents: "none",
        backgroundImage:
          "linear-gradient(rgba(59, 130, 246, 0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(59, 130, 246, 0.025) 1px, transparent 1px)",
        backgroundSize: "60px 60px",
        zIndex: 0,
      }}
    />
    {/* Glow accent */}
    <Box
      sx={{
        position: "absolute", top: 0, left: "50%",
        transform: "translateX(-50%)",
        width: 600, height: 200,
        background: "radial-gradient(ellipse, rgba(37, 99, 235, 0.08) 0%, transparent 70%)",
        filter: "blur(40px)", zIndex: 0, pointerEvents: "none",
      }}
    />

    <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "2fr 1fr 1fr 1.5fr" },
          gap: { xs: 5, md: 4 },
          mb: { xs: 6, md: 8 },
        }}
      >
        {/* ── Column 1: Brand ── */}
        <Box>
          <Box
            component={RouterLink}
            href={paths.index}
            sx={{ display: "inline-block", mb: 2.5, textDecoration: "none" }}
          >
            <Box
              component="img"
              src="/assets/swift-ramp-logo.png"
              alt="Platinx Exchange"
              sx={{ height: 52, width: "auto", objectFit: "contain" }}
            />
          </Box>
          <Typography
            sx={{
              color: "rgba(255,255,255,0.5)",
              fontSize: "0.875rem",
              lineHeight: 1.75,
              maxWidth: 280,
              mb: 3.5,
            }}
          >
            Platinx Exchange — India's trusted platform for international remittance and institutional OTC cryptocurrency trading.
          </Typography>

          {/* Socials */}
          <Stack direction="row" spacing={1.5}>
            {socials.map((s) => (
              <Link
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={s.label}
                sx={{ textDecoration: "none" }}
              >
                <Box
                  sx={{
                    width: 38, height: 38, borderRadius: "50%",
                    background: "rgba(255,255,255,0.05)",
                    border: "1px solid rgba(255,255,255,0.09)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: "rgba(255,255,255,0.5)",
                    transition: "all 0.25s ease",
                    "&:hover": {
                      background: `${s.color}1A`,
                      borderColor: s.color,
                      color: s.color,
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  {s.icon}
                </Box>
              </Link>
            ))}
          </Stack>
        </Box>

        {/* ── Column 2: Company ── */}
        <Box>
          <ColHead>Company</ColHead>
          <Stack spacing={1.5}>
            {company.map((item) => (
              <Box
                key={item.title}
                component="button"
                onClick={() => scrollTo(item.id)}
                sx={{
                  background: "none", border: "none", cursor: "pointer",
                  textAlign: "left", p: 0,
                  color: "rgba(255,255,255,0.55)",
                  fontSize: "0.875rem",
                  fontWeight: 400,
                  transition: "color 0.2s ease",
                  "&:hover": { color: "#60A5FA" },
                }}
              >
                {item.title}
              </Box>
            ))}
          </Stack>
        </Box>

        {/* ── Column 3: Legal ── */}
        <Box>
          <ColHead>Legal</ColHead>
          <Stack spacing={1.5}>
            {legal.map((item) => (
              <Link
                key={item.title}
                component={RouterLink}
                href={item.path}
                sx={{
                  color: "rgba(255,255,255,0.55)",
                  fontSize: "0.875rem",
                  textDecoration: "none",
                  display: "block",
                  transition: "color 0.2s ease",
                  "&:hover": { color: "#60A5FA" },
                }}
              >
                {item.title}
              </Link>
            ))}
          </Stack>
        </Box>

        {/* ── Column 4: Contact ── */}
        <Box>
          <ColHead>Contact</ColHead>
          <Stack spacing={2.5}>
            {[
              {
                icon: <PhoneOutlinedIcon sx={{ fontSize: 16 }} />,
                value: "+91 9560107106",
                href: "tel:+919560107106",
              },
              {
                icon: <EmailOutlinedIcon sx={{ fontSize: 16 }} />,
                value: "support@platinx.exchange",
                href: "mailto:support@platinx.exchange",
              },
              {
                icon: <LocationOnOutlinedIcon sx={{ fontSize: 16 }} />,
                value: "Unit 1919, 19th Floor, Tower A,\nSpectrum Metro Mall,\nSector 75, Noida-201301, India",
                href: "https://www.google.com/maps/place/Spectrum+@+Metro,+Sector+75,+Noida,+Uttar+Pradesh+201301,+India",
              },
            ].map((c, i) => (
              <Link
                key={i}
                href={c.href}
                target={c.href.startsWith("http") ? "_blank" : undefined}
                rel="noopener noreferrer"
                sx={{ textDecoration: "none", display: "block" }}
              >
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <Box
                    sx={{
                      color: "#3B82F6",
                      mt: "2px",
                      flexShrink: 0,
                      transition: "color 0.2s ease",
                    }}
                  >
                    {c.icon}
                  </Box>
                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.55)",
                      fontSize: "0.85rem",
                      lineHeight: 1.6,
                      whiteSpace: "pre-line",
                      transition: "color 0.2s ease",
                      "&:hover": { color: "#60A5FA" },
                    }}
                  >
                    {c.value}
                  </Typography>
                </Stack>
              </Link>
            ))}
          </Stack>
        </Box>
      </Box>

      {/* ── Bottom Bar ── */}
      <Divider sx={{ borderColor: "rgba(59, 130, 246, 0.1)", mb: 3.5 }} />
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={1.5}
      >
        <Typography sx={{ color: "rgba(255,255,255,0.28)", fontSize: "0.8rem" }}>
          © {new Date().getFullYear()} Platinx Exchange. All rights reserved.
        </Typography>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#10B981", boxShadow: "0 0 6px #10B981" }} />
          <Typography sx={{ color: "rgba(255,255,255,0.28)", fontSize: "0.8rem" }}>
            All systems operational
          </Typography>
        </Stack>
      </Stack>
    </Container>
  </Box>
);
